/* GPS tracker — wraps Geolocation.watchPosition with a debounced interval,
   or a simulator that walks a predefined route for testing. */

import { distance } from './geo';

const DEFAULT_INTERVAL_MS = 3000;
const MIN_MOVE_METERS = 2; // ignore jitter below this

export class GpsTracker {
  constructor({ intervalMs = DEFAULT_INTERVAL_MS, onPoint, onError, minMove = MIN_MOVE_METERS } = {}) {
    this.intervalMs = intervalMs;
    this.onPoint = onPoint || (() => {});
    this.onError = onError || (() => {});
    this.minMove = minMove;
    this.watchId = null;
    this.simTimer = null;
    this.simIndex = 0;
    this.simRoute = null;
    this.simMode = false;
    this.lastPoint = null;
    this.lastEmit = 0;
    this.paused = false;
  }

  isSupported() {
    return typeof navigator !== 'undefined' && 'geolocation' in navigator;
  }

  startReal() {
    if (!this.isSupported()) {
      this.onError({ code: 'unsupported', message: 'Geolocation not supported' });
      return;
    }
    this.simMode = false;
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this._handleReal(pos),
      (err) => this.onError({ code: err.code, message: err.message }),
      { enableHighAccuracy: true, maximumAge: 1500, timeout: 15000 }
    );
  }

  _handleReal(pos) {
    if (this.paused) return;
    const now = Date.now();
    if (now - this.lastEmit < this.intervalMs) return;
    const p = {
      t: pos.timestamp || now,
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      speed: pos.coords.speed,
      heading: pos.coords.heading,
    };
    if (this.lastPoint && distance(this.lastPoint, p) < this.minMove) {
      // still emit position update for UI but flag as no-record
      this.onPoint(p, { record: false });
      return;
    }
    this.lastPoint = p;
    this.lastEmit = now;
    this.onPoint(p, { record: true });
  }

  startSimulator(route, { speedFactor = 1 } = {}) {
    this.simMode = true;
    this.simRoute = route;
    this.simIndex = 0;
    const stepMs = Math.max(500, this.intervalMs / speedFactor);
    const tick = () => {
      if (this.paused) return;
      if (!this.simRoute || this.simIndex >= this.simRoute.length) return;
      const raw = this.simRoute[this.simIndex++];
      const p = {
        t: Date.now(),
        lat: raw.lat,
        lng: raw.lng,
        accuracy: raw.accuracy ?? 5,
        altitude: raw.altitude ?? null,
        speed: raw.speed ?? 1.2,
        heading: raw.heading ?? null,
      };
      this.lastPoint = p;
      this.onPoint(p, { record: true, simulated: true });
    };
    tick();
    this.simTimer = setInterval(tick, stepMs);
  }

  pause() { this.paused = true; }
  resume() { this.paused = false; }

  stop() {
    if (this.watchId != null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.simTimer) {
      clearInterval(this.simTimer);
      this.simTimer = null;
    }
  }
}

/* One-shot GPS read to prime "current position" without starting a watch. */
export function readOnce() {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('unsupported'));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        t: pos.timestamp,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        altitude: pos.coords.altitude,
        speed: pos.coords.speed,
        heading: pos.coords.heading,
      }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 }
    );
  });
}
